import React, { memo } from "react";
import { cn } from "../../../../../services/common/className";
import Collapsible from "ui-components/collapsible/collapsible";
import Tag from "ui-components/molecules/tag/tag";
import {
  DispatchBoardQueueLoadDetail,
  DispatchBoardQueueLoadDetailProbill,
  DispatchBoardQueueLoadDetailSequenceType,
  ProbillStatus,
} from "app/data/city-delivery/dispatch-board/models";
import CityDeliveryLoadDetailItem from "./cityDeliveryLoadDetailItem";
import "./cityDeliveryLoadDetailStop.scss";

function getSequenceTypeLabel(sequenceType: DispatchBoardQueueLoadDetailSequenceType): string {
  return sequenceType === DispatchBoardQueueLoadDetailSequenceType.LOADING ? "Loading" : "Delivery";
}

/** Maps probill status to a BEM modifier value for collapsible/Tag styling (green/blue/grey/red). */
function getProbillStatusMod(probillStatus: ProbillStatus | undefined): "delivered" | "loaded" | "in_progress" | "pending" | "exceptions" {
  if (!probillStatus) return "pending";
  switch (probillStatus) {
    case ProbillStatus.DELIVERED:
      return "delivered";
    case ProbillStatus.LOADED:
      return "loaded";
    case ProbillStatus.IN_PROGRESS:
      return "in_progress";
    case ProbillStatus.EXCEPTIONS:
      return "exceptions";
    default:
      return "pending";
  }
}

export interface CityDeliveryLoadDetailStopProps {
  stop: DispatchBoardQueueLoadDetail;
}

const CityDeliveryLoadDetailStop: React.FC<CityDeliveryLoadDetailStopProps> = memo(({ stop }) => {
  const stopLabel = `Stop ${stop.sequenceNumber} – ${getSequenceTypeLabel(stop.sequenceType)}`;

  return (
    <article className="city-delivery-load-detail-stop">
      <header className="city-delivery-load-detail-stop__head">
        <h4 className="city-delivery-load-detail-stop__title">
          <b>{stopLabel}</b>
        </h4>
        <span className="city-delivery-load-detail-stop__line" />
      </header>
      <ul className="city-delivery-load-detail-stop__body">
        {stop.probills?.length
          ? stop.probills.map((probill: DispatchBoardQueueLoadDetailProbill, probillIdx: number) => {
              const statusMod = getProbillStatusMod(probill.probillStatus);
              return (
              <li
                key={`probill-${probill.probillNumber}-${probillIdx}`}
                className="city-delivery-load-detail-stop__probill"
              >
                <Collapsible
                  mix={cn("city-delivery-load-detail-stop__collapsible")({ probillStatus: statusMod })}
                  header={
                    <div className="city-delivery-load-detail-stop__header">
                      <div className="city-delivery-load-detail-stop__header__identifiers">
                        <div className="city-delivery-load-detail-stop__header__identifiers__item">
                          Probill:{" "}
                          <a
                            className="probill-number"
                            href={`/shipments/${probill.probillNumber}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {probill.probillNumber}
                          </a>
                        </div>
                        <div className="city-delivery-load-detail-stop__header__identifiers__item">
                          BOL: {probill.bolNumber}
                        </div>
                        <div className="city-delivery-load-detail-stop__header__identifiers__item">
                          PO: {probill.poNumber}
                        </div>
                        <Tag mix={cn("city-delivery-load-detail-stop__probill-status")(statusMod)}>
                          {(probill.probillStatus ?? ProbillStatus.PENDING).replace(/_/g, " ")}
                        </Tag>
                      </div>
                      <div className="city-delivery-load-detail-stop__header__info-group">
                        <div className="city-delivery-load-detail-stop__header__info-group__item city-delivery-load-detail-stop__header__info-group__item--col1">
                          <span className="city-delivery-load-detail-stop__header__info-group__item--text-light">
                            Shipper:
                          </span>{" "}
                          {`${probill.shipperName} (${probill.shipperNumber})`}
                        </div>
                        <div className="city-delivery-load-detail-stop__header__info-group__item city-delivery-load-detail-stop__header__info-group__item--col2">
                          <span className="city-delivery-load-detail-stop__header__info-group__item--text-light">
                            Consignee:
                          </span>{" "}
                          {`${probill.consigneeName} (${probill.consigneeNumber})`}
                        </div>
                      </div>
                      <div className="city-delivery-load-detail-stop__header__info-group">
                        <div className="city-delivery-load-detail-stop__header__info-group__item city-delivery-load-detail-stop__header__info-group__item--col1">
                          <span className="city-delivery-load-detail-stop__header__info-group__item--text-light">
                            Route Name:
                          </span>{" "}
                          {probill.routeName != null && probill.routeName !== "" ? probill.routeName : "-"}
                        </div>
                        <div className="city-delivery-load-detail-stop__header__info-group__item city-delivery-load-detail-stop__header__info-group__item--col2">
                          <span className="city-delivery-load-detail-stop__header__info-group__item--text-light">
                            Route Number:
                          </span>{" "}
                          {probill.routeNumber != null ? probill.routeNumber : (probill.route != null ? probill.route : "-")}
                        </div>
                      </div>
                      <div className="city-delivery-load-detail-stop__header__info-group">
                        <div className="city-delivery-load-detail-stop__header__info-group__item city-delivery-load-detail-stop__header__info-group__item--col1">
                          <span className="city-delivery-load-detail-stop__header__info-group__item--text-light">
                            Cons Address:
                          </span>{" "}
                          {probill.consigneeAddress}
                        </div>
                        <div className="city-delivery-load-detail-stop__header__info-group__item city-delivery-load-detail-stop__header__info-group__item--col2" />
                      </div>
                    </div>
                  }
                  iconType="normal"
                >
                  <div className="city-delivery-load-detail-stop__list">
                    {probill.items?.length
                      ? probill.items.map((item, itemIdx) => (
                          <li
                            className="city-delivery-load-detail-stop__list-item"
                            key={`item-${item.itemSequenceNumber}-${itemIdx}`}
                          >
                            <CityDeliveryLoadDetailItem item={item} />
                          </li>
                        ))
                      : <div className="city-delivery-load-detail-stop__list-empty">No items</div>}
                  </div>
                </Collapsible>
              </li>
              );
            })
          : <div className="city-delivery-load-detail-stop__empty">No probills</div>}
      </ul>
    </article>
  );
});

CityDeliveryLoadDetailStop.displayName = "CityDeliveryLoadDetailStop";

export default CityDeliveryLoadDetailStop;
